import { Alert, Card, Flex, Table } from "antd";
import { useEffect, useState } from "react";
import Filters from "../../components/Filters";
import MainMetrics from "../../components/MainMetrics";
import TablesWrapper from "../../components/TablesWrapper";
import { useAuth } from "../../context/AuthContext";
import useAttentionV2 from "../../hooks/useAttentionV2";
import useCollections from "../../hooks/useCollections";
import useDateRangeWorkingHours from "../../hooks/useDateRangeWorkingHours";
import useHomeConversionV2 from "../../hooks/useHomeConversionV2";
import useQueueV2 from "../../hooks/useQueueV2";
import {
  FLOW_DETECTIONS_METRICS,
  FLOW_DETECTIONS_TABLE_COLUMNS,
  QUEUE_ZONES_TABLE_COLUMNS,
} from "../../utils/constants";
import { periodoSemiaberto } from "../general/homeV2Helpers";
import { filtrosDaV2 } from "../revenue/kpisDaV2";
import BarChartWrapper from "./BarChartWrapper";
import HeatMapWrapper from "./HeatMapWrapper";
import TimeSeriesChartWrapper from "./TimeSeriesChartWrapper";
import {
  barrasDoFluxo,
  cartoesDoFluxo,
  filtrosDaAtencao,
  indisponiveisDoFluxo,
  serieDoFluxo,
  tabelasDoFluxo,
  temDadoDeZona,
} from "./fluxoDaV2";

export default function FlowDetectionsRefact() {
  const { user } = useAuth();
  const branchId = user?.active_branch_id ?? null;
  const { modoOuran, legacyIdDaColecao } = useCollections();
  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);
  const [selectedPeriod, setSelectedPeriod] = useState([9, 19]);
  const [selectedZones, setSelectedZones] = useState(["all"]);
  const [selectedCollections, setSelectedCollections] = useState(["all"]);
  const { minHour: periodMinHour, maxHour: periodMaxHour } =
    useDateRangeWorkingHours(selectedDateRange);

  useEffect(() => {
    if (!selectedDateRange?.[0] || !selectedDateRange?.[1]) return;
    setSelectedPeriod([periodMinHour, periodMaxHour]);
  }, [selectedDateRange, periodMinHour, periodMaxHour]);

  const periodo = periodoSemiaberto(selectedDateRange);
  const { data: queueData, isLoading: isQueueDataLoading } = useQueueV2({
    branchId,
    start: periodo.start,
    end: periodo.end,
  });
  const filtrosDaTela = filtrosDaAtencao(
    filtrosDaV2({
      zonas: selectedZones,
      colecoes: selectedCollections,
      modoOuran,
      legacyIdDaColecao,
    })
  );
  const atencao = useAttentionV2({
    branchId,
    start: periodo.start,
    end: periodo.end,
    groupBy: "zone",
    filtros: filtrosDaTela,
  });
  const fluxoPorMinuto = useAttentionV2({
    branchId,
    start: periodo.start,
    end: periodo.end,
    groupBy: "minute",
    filtros: filtrosDaTela,
  });
  const conversao = useHomeConversionV2({
    branchIds: branchId ? [branchId] : [],
    start: periodo.start,
    end: periodo.end,
  });
  const isV2Loading = atencao.isLoading || conversao.isLoading;
  const isV2Error = atencao.isError || conversao.isError;

  const kpiData =
    atencao.data && conversao.data
      ? cartoesDoFluxo({
          atencao: atencao.data,
          conversao: conversao.data,
          fila: queueData,
          filtros: filtrosDaTela,
        })
      : undefined;

  const activeKpis =
    !isQueueDataLoading && !queueData?.tem_zona_de_fila
      ? FLOW_DETECTIONS_METRICS.filter((m) => m.key !== "avg_queue_wait")
      : FLOW_DETECTIONS_METRICS;

  // Filial sem zona cadastrada ainda conta entradas — a contagem vem das linhas
  // e não depende de zona. As demais métricas desta tela dependem, e mostrar 0
  // nelas seria afirmar uma medição que não existe.
  const hasZoneData = temDadoDeZona(atencao.data);

  // Numa falha de carregamento nenhuma métrica é conhecida, nem as entradas.
  const zoneMetricKeys = isV2Error
    ? FLOW_DETECTIONS_METRICS.map((m) => m.key)
    : [
        ...new Set([
          ...indisponiveisDoFluxo(kpiData),
          ...(hasZoneData ? [] : ["max_people"]),
        ]),
      ];
  const tabelas = atencao.data
    ? tabelasDoFluxo(atencao.data, filtrosDaTela)
    : undefined;
  const barras = atencao.data ? barrasDoFluxo(atencao.data) : undefined;

  return (
    <Flex
      style={{ width: "100%", margin: "0 auto" }}
      justify="center"
      align="center"
      vertical
      gap={24}>
      <Filters
        page="flow_detections"
        isMainDataLoading={isV2Loading}
        filters={["data", "periodo"]}
        dateRange={true}
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={setSelectedDateRange}
        selectedPeriod={selectedPeriod}
        setSelectedPeriod={setSelectedPeriod}
        periodMin={periodMinHour}
        periodMax={periodMaxHour}
        selectedZones={selectedZones}
        setSelectedZones={setSelectedZones}
        selectedCollections={selectedCollections}
        setSelectedCollections={setSelectedCollections}
      />
      <MainMetrics
        data={kpiData}
        isLoading={isQueueDataLoading || isV2Loading}
        kpis={activeKpis}
        unavailableKeys={zoneMetricKeys}
      />
      {!isV2Loading && isV2Error && (
        <Alert
          style={{ width: "85%", maxWidth: "100%" }}
          type="error"
          showIcon
          message="Não foi possível carregar as métricas deste período."
          description="Os valores acima não foram medidos. Tente novamente ou selecione outro período."
        />
      )}
      {!isV2Loading && !isV2Error && !hasZoneData && (
        <Alert
          style={{ width: "85%", maxWidth: "100%" }}
          type="info"
          showIcon
          message="Sem dados de zona no período selecionado."
          description="As oportunidades vêm das linhas de entrada e continuam disponíveis. Ocupação, pico de pessoas e tempo de atenção dependem de detecção nas zonas. Se alguma zona foi cadastrada há pouco, o dado passa a aparecer a partir da próxima rodada do processamento."
        />
      )}
      <HeatMapWrapper
        selectedDateRange={selectedDateRange}
        selectedPeriod={selectedPeriod}
      />
      <TimeSeriesChartWrapper
        data={fluxoPorMinuto.data ? serieDoFluxo(fluxoPorMinuto.data) : null}
        isLoading={fluxoPorMinuto.isLoading}
      />
      <BarChartWrapper
        data={barras}
        maxZones={barras?.labels?.length || 0}
        isLoading={atencao.isLoading}
      />
      <TablesWrapper
        data={tabelas}
        isLoading={atencao.isLoading}
        columns={FLOW_DETECTIONS_TABLE_COLUMNS}
        tablesName={[
          "Zonas com fluxo acima da media",
          "Zonas com fluxo abaixo da media",
        ]}
      />
      {(queueData?.tem_zona_de_fila || isQueueDataLoading) && (
        <div style={{ width: "85%", maxWidth: "100%" }}>
          <Card
            title="Zonas de Fila"
            align="center"
            loading={isQueueDataLoading}
            styles={{
              header: {
                backgroundColor: "var(--ouran-card-header-bg)",
              },
            }}>
            <Table
              dataSource={queueData?.zonas ?? []}
              columns={QUEUE_ZONES_TABLE_COLUMNS}
              rowKey="zone_id"
              pagination={{ pageSize: 10 }}
              scroll={{ x: "max-content" }}
            />
          </Card>
        </div>
      )}
    </Flex>
  );
}
